import { Card, CardHeader, Button, Stack, TextField, InputAdornment, CircularProgress, Box, Drawer, useMediaQuery, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { ReduxState } from "ra-core";
import { ChangeEventHandler, FC, useCallback, useState } from "react";
import { debounce } from "lodash";
import { useSelector } from "react-redux";
import { useResourceRegister } from "@/utils/resourceRegister";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_ONDATA_CLIENTS } from "@/constants";
import { useClientManageContextController, ClientManageContextProvider } from "@/components/settings/clients/ClientManageContext";
import DriverEncryptToolsList from "@/components/settings/clients/DriverEncryptToolsList";
import SecurefileAppsList from "@/components/settings/clients/SecurefileAppsList";
import SearchIcon from '@/icons/Search';
import SecurefileRegisterCode from "@/components/settings/clients/SecurefileRegisterCodes";
import { useHasFeature } from "@/utils/useHasFeature";


const clientsDriverRes = 'settings-clients-driver-encrypttool'
const clientsSecurefileRes = 'settings-clients-securefile'


const ClientManage: FC = () => {
  useResourceRegister({ name: clientsDriverRes })
  useResourceRegister({ name: clientsSecurefileRes })
  useResourceRegister({ name: 'settings-clients-securefile-register-code' })
  useResourceRegister({ name: 'settings-clients-securefile-config' })
  useResourceRegister({ name: 'settings-clients-securefile-customkey-config' })
  useResourceRegister({ name: 'settings-client-force-refresh-key' })
  useResourceRegister({ name: 'settings-client-share-secret' })
  useResourceRegister({ name: 'org-roles' })
  useResourceRegister({ name: 'client-access-policy' })

  const hasWritePermit = useHasPermit([MANAGE_ONDATA_CLIENTS])
  const hasFeature = useHasFeature()
  
  const clientManageCtrls = useClientManageContextController(hasFeature.OnDataSecureDB ? (hasFeature.OnDataDriver ? 'drivers' : 'encrypttool') : 'securefile')
  const { 
    states:{ activeListCtx }, 
    setActiveListCtx,
    setTerm,
  } = clientManageCtrls
  
  const loading = useSelector<ReduxState, boolean>(store => !!store.admin.loading)

  const [states, setStates] = useState({
    term: '',
    openAddDriver: false,
    openRegisterCodes: false,
  });

  const debouncedSetTerm = useCallback(debounce(setTerm, 300), [setTerm])
  const handleTermChange : ChangeEventHandler<HTMLInputElement> = (e)=>{
    debouncedSetTerm(e.target.value)
    setStates(v => ({...v, term: e.target.value}))
  }

  const match = useMediaQuery('(max-width: 1400px)')
  const width = match ? 'calc(100vw - 150px)' : 1250

  return (
    <ClientManageContextProvider value={clientManageCtrls}>
      <Card sx={{ pt: 0.75 }}>
        <CardHeader title={
          <Box>
            <Stack direction="row" alignItems="center" 
              sx={{ 
                '& > div.MuiBox-root': { p: 0 },
                '& > button': { ml: 1.5 }
              }}
            >
              <ToggleButtonGroup
                color="primary"
                value={activeListCtx}
                size="small"
                exclusive
                onChange={(e, v) => setActiveListCtx(v)}
                aria-label="Platform"
                sx={{ mr: 'auto', 
                  '& button': {
                    color: theme => theme.palette.text.secondary,
                  }
                }}
              >
                {hasFeature.OnDataDriver &&
                  <ToggleButton value="drivers">Database Driver</ToggleButton>}
                {hasFeature.OnDataSecureDB &&
                  <ToggleButton value="encrypttool">Encryption Tool</ToggleButton>}
                {hasFeature.OnDataSecureFile &&
                  <ToggleButton value="securefile">SecureFile App</ToggleButton>}
              </ToggleButtonGroup>

              {hasWritePermit && hasFeature.OnDataDriver     && activeListCtx === 'drivers' &&
                <Button variant="contained" size="small" onClick={() => setStates(v => ({...v, openAddDriver: true}))}>
                  Add New...
                </Button>}
              {hasWritePermit && hasFeature.OnDataSecureFile && activeListCtx === 'securefile' &&
                <>
                  <Button variant="contained" size="small" onClick={() => setStates(v => ({...v, openRegisterCodes: true}))}>
                    Registration Code...
                  </Button>
                </>}
            </Stack>

            <TextField
              id="client-name-search-input"
              placeholder='Search by Client Name'
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {loading ? <CircularProgress size={20} /> : <SearchIcon fontSize="small" />}
                  </InputAdornment>
                )
              }}
              onChange={handleTermChange}
              value={states.term}
              variant="outlined"
              sx={{ width: 280, mt: 2 }}
            />
            
          </Box>
        } />
        
        {activeListCtx !== 'securefile' &&
          <DriverEncryptToolsList 
            openAdd={states.openAddDriver} 
            setOpenAdd={(x)=>setStates(v => ({...v, openAddDriver:x}))} />}
        
        {activeListCtx === 'securefile' && hasFeature.OnDataSecureFile && 
          <>
            <SecurefileAppsList />
            <Drawer
              anchor="right"
              onClose={()=>{ setStates(v => ({...v, openRegisterCodes: false})) }}
              open={states.openRegisterCodes}
              sx={{ zIndex: theme => theme.zIndex.appBar - 5 }}
              PaperProps={{
                sx: {
                  p: 2,
                  width: width,
                  bgcolor: 'background.default',
                  backgroundImage: 'none',
                },          
              }}
            >
              <Box sx={{ pt: 6 }}>
                <SecurefileRegisterCode {...({close: () => setStates(v => ({...v, openRegisterCodes: false}))} as any)} />
              </Box>
            </Drawer>
          </>}
        
        
      </Card>
    </ClientManageContextProvider>
  )
}

export default ClientManage;
